// mockStream — native v2 활동지 shapes 직접 emit.
// SKILL.md (`.claude/skills/task-editor/SKILL.md`) 가 진실 공급원.
// legacy migration 없음. @ts-nocheck 없음.
import type {
  ActionRequest,
  Decision,
  ResumeRequest,
  SSEEvent,
  StreamRequest,
} from "./types";
import type {
  TaskEditorState,
  TaskEditorBlock,
  TaskEditorBlockType,
  TaskEditorDescriptionComponent,
  TaskEditorActionComponent,
  TaskEditorButtonOption,
  TaskEditorTextInputField,
} from "../utils/taskEditorUtils";
import { migrateTaskEditorStateFromLegacy } from "../utils/taskEditorUtils";

// mock 빌드 함수용 helper — block.type 을 생략하더라도 normalize 단계에서
// description[0] 의 typography_style 에 맞춰 자동으로 채워준다.
type MockBlockInput = Omit<TaskEditorBlock, "type"> & {
  type?: TaskEditorBlockType;
};
type MockTaskInput = Omit<TaskEditorState, "blocks"> & {
  blocks: MockBlockInput[];
};
function makeMockTask(input: MockTaskInput): TaskEditorState {
  return migrateTaskEditorStateFromLegacy(input);
}

// 활동지 v2 퀴즈 오답 메시지 — 시나리오 전체 일관 사용
const QUIZ_INCORRECT = "활동지를 천천히 살펴본 뒤 다시 답해보세요.";

interface MockSession {
  currentTask: TaskEditorState | null;
  pendingAction?: ActionRequest;
}

const sessions = new Map<string, MockSession>();

function getSession(threadId: string): MockSession {
  let s = sessions.get(threadId);
  if (!s) {
    s = { currentTask: null };
    sessions.set(threadId, s);
  }
  return s;
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const t = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(t);
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

const rid = () => Math.random().toString(36).slice(2, 10);

// 단순 패스스루 — native v2 shape 이므로 변환 불필요
function ev<E extends SSEEvent["event"]>(
  event: E,
  data: Extract<SSEEvent, { event: E }>["data"]
): SSEEvent {
  return { event, data } as SSEEvent;
}

function chunkText(text: string): string[] {
  const chunks: string[] = [];
  let buf = "";
  for (const ch of text) {
    buf += ch;
    if (buf.length >= 2 + Math.floor(Math.random() * 3)) {
      chunks.push(buf);
      buf = "";
    }
  }
  if (buf) chunks.push(buf);
  return chunks;
}

// =========================
// Mock guide previews (v2 어휘)
// =========================

const MOCK_GUIDES = {
  components: `# TaskEditor v2 컴포넌트 카탈로그

## 블록 (Block)
블록은 type/id 없음. description 배열 + action 객체(또는 null) 로 구성.
- description[0] 은 반드시 text 컴포넌트 — 삭제·재정렬·타입 변경 불가.
- description.length >= 1, 그 중 text >= 1.
- action 은 객체 1개 또는 null (max 1 강제).

## 컴포넌트 8종 (데이터 type)

| type                        | 섹션        | UI 카테고리  |
|-----------------------------|-------------|--------------|
| text                        | description | 텍스트       |
| image                       | description | 이미지       |
| reference_automatic_thought | description | 불러오기     |
| reference_coach_preset      | description | 불러오기     |
| text_input                  | action      | 텍스트 입력  |
| button_default              | action      | 버튼         |
| button_quiz                 | action      | 버튼         |
| button_branch               | action      | 버튼         |

## text 컴포넌트 typography_style
- header: 섹션 제목
- body: 일반 본문 (기본)
- label: 입력 필드 바로 위 질문/라벨

## button_quiz 규칙
- options[] 에 is_correct: true 최소 1개
- incorrect_message 필수
- 정답 해설은 quiz 바로 다음 블록의 text(body) 로 제공

## button_branch 분기 참조
show_from / show_to / restart_from / hide_from 은 동일 활동지의 block sequence (1-based).`,

  structure: `# 활동지 v2 구조 원칙

## 최상위 필드 (TaskEditorState, snake_case)
- task_id: 영문 kebab-case 또는 숫자 식별자 (예: "day1-start-of-change")
- title: 한국어 짧은 제목
- set_number: 정수 (optional)
- is_key_activity: bool (optional)
- blocks: 비어 있으면 안 됨

## ID 없음
- 블록·컴포넌트 모두 id 필드 없음 — 위치는 배열 index 또는 1-based sequence 로 식별.
- LastCard 개념 없음 — 활동지 종료 UI 는 런타임 책임.

## 블록 invariant 3개
1. description[0] 은 text 고정
2. description.length >= 1, text >= 1
3. action 은 객체 1개 또는 null

## 블록 구성 패턴
1. 섹션 시작: description[0] = text(header, 섹션 제목)
2. 본문: 같은 블록의 description 에 text(body) 추가
3. 입력 라벨: text(label) 이어서 배치 후 action 설정
4. Quiz: description: [text(label, 질문)], action: button_quiz
5. 정답 해설: 다음 블록 description: [text(body, 해설)], action: null

## button_branch 분기 규칙
- show_from/show_to/restart_from/hide_from = block sequence (1-based)
- 분기 대상 블록은 질문 블록 바로 뒤에 배치
- 편집 시 변경되지 않은 부분은 그대로 보존`,

  styles: `# typography_style 가이드

- header: 섹션 제목 (블록의 description[0] 에 주로 사용)
- body: 일반 본문/안내 (기본)
- label: 입력 필드 바로 위 질문/라벨. Quiz 질문, text_input 앞에 권장.

3종만 사용. Alert/Dimmed 는 v2 에서 제거됨.`,
};

// =========================
// Mutation helpers
// =========================

function buildSeedTask(): TaskEditorState {
  return makeMockTask({
    task_id: "mock-task-" + rid(),
    title: "(mock) 새 활동지",
    set_number: 1,
    blocks: [
      {
        description: [
          {
            type: "text",
            content: "여기 본문이 들어갑니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
    ],
  });
}

// session.currentTask 의 메타데이터 (사용자가 메타데이터 모달에서 정한 값)
// 가 raw text 시나리오의 mock 기본값을 덮어쓰지 않도록 보존한다.
// 빈 session 일 때만 fallback task_id 를 부여한다.
function mergeSessionMetadata(
  generated: TaskEditorState,
  session: TaskEditorState | null
): TaskEditorState {
  if (session && session.task_id.trim().length > 0) {
    return {
      ...generated,
      task_id: session.task_id,
      title: session.title || generated.title,
      set_number: session.set_number ?? generated.set_number,
      is_key_activity: session.is_key_activity ?? generated.is_key_activity,
    };
  }
  return {
    ...generated,
    task_id: "mock-" + rid().slice(0, 6),
  };
}

function applyMockEdit(
  task: TaskEditorState | null,
  action: { name: string; args: Record<string, unknown> }
): TaskEditorState | null {
  if (action.name === "write_file") {
    const content = action.args.content;
    if (typeof content === "string") {
      try {
        return JSON.parse(content) as TaskEditorState;
      } catch {
        return task;
      }
    }
    return task;
  }
  if (action.name === "edit_file" && task) {
    const oldStr = String(action.args.old_string ?? "");
    const newStr = String(action.args.new_string ?? "");
    const json = JSON.stringify(task);
    const replaced = json.split(oldStr).join(newStr);
    try {
      return JSON.parse(replaced) as TaskEditorState;
    } catch {
      return task;
    }
  }
  return task;
}

// =========================
// Scenario routing
// =========================

type Scenario =
  | "rawToBeginning"
  | "rawToDistancing"
  | "rawToExperienceSeparation"
  | "branchingFlow"
  | "validateTask"
  | "addQuiz"
  | "removeLastBlock"
  | "generic";

function pickScenario(message: string): Scenario {
  const m = message.toLowerCase();

  // 분기 / branching
  if (/(분기|buttongroup .?(추가|넣)|branching|갈래)/.test(m)) {
    return "branchingFlow";
  }
  // 검증 / lint
  if (/(검증|validate|lint|규칙.?확인|체크.?해)/.test(m)) {
    return "validateTask";
  }
  // 긴 글 (200자 이상) 또는 명시적 키워드 → raw 변환
  if (message.length >= 200 || /활동지로 만들|구조화|블록으로 나눠/.test(m)) {
    // 1) raw text 의 제목 라인을 우선 매칭 — 본문에 다른 시나리오 키워드가
    //    섞여 있어도 잘못 라우팅되지 않도록 시그니처 기반으로 먼저 판단한다.
    if (/제목:\s*변화의 시작/.test(message)) {
      return "rawToBeginning";
    }
    if (/거리두기란 무엇인가/.test(message)) {
      return "rawToDistancing";
    }
    if (/기술 1:\s*경험 분리/.test(message)) {
      return "rawToExperienceSeparation";
    }

    // 2) 자유 입력 fallback — 구체적 키워드 → 일반 키워드 순으로.
    if (
      /경험 분리|경험 ?꾸러미|악순환|실전 연습|경험분리|복잡한 경험/.test(
        message
      )
    ) {
      return "rawToExperienceSeparation";
    }
    if (
      /변화의 시작|위대한 선택|마법의 알약|오래된 펌프|100일|마음속 타인/.test(
        message
      )
    ) {
      return "rawToBeginning";
    }
    if (
      /마음의 선글라스|신성한 멈춤|마음의 rain|건강한 마음에 대한 오해|새로운 관계|거리두기/i.test(
        message
      )
    ) {
      return "rawToDistancing";
    }
    // fallback
    return "rawToBeginning";
  }
  if (/(quiz|퀴즈)/.test(m)) return "addQuiz";
  if (/(삭제|remove|delete|지우|제거)/.test(m)) return "removeLastBlock";
  return "generic";
}

// =========================
// Stream entry
// =========================

export async function* mockStreamTaskAuthoring(
  req: StreamRequest,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const tid = req.thread_id;
  const session = getSession(tid);

  // 매 turn 마다 최신 current_task 로 동기화 (사용자 편집 반영)
  if (req.current_task !== undefined) {
    session.currentTask = req.current_task;
  }

  const scenario = pickScenario(req.message);

  yield ev("session", { thread_id: tid });
  await sleep(180, signal);

  switch (scenario) {
    case "rawToBeginning":
      yield* rawToBeginningScenario(tid, req.message, signal);
      return;
    case "rawToDistancing":
      yield* rawToDistancingScenario(tid, req.message, signal);
      return;
    case "rawToExperienceSeparation":
      yield* rawToExperienceSeparationScenario(tid, req.message, signal);
      return;
    case "branchingFlow":
      yield* branchingFlowScenario(tid, signal);
      return;
    case "validateTask":
      yield* validateTaskScenario(tid, signal);
      return;
    case "addQuiz":
      yield* addQuizScenario(tid, signal);
      return;
    case "removeLastBlock":
      yield* removeLastBlockScenario(tid, signal);
      return;
    default:
      yield* genericScenario(tid, signal);
      return;
  }
}

// =========================
// Common helpers
// =========================

function ensureCurrentTask(session: MockSession): TaskEditorState {
  if (!session.currentTask) {
    session.currentTask = buildSeedTask();
  }
  return session.currentTask;
}

async function* streamReply(
  reply: string,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  for (const c of chunkText(reply)) {
    if (signal?.aborted) return;
    yield ev("message_chunk", { delta: c });
    await sleep(35, signal);
  }
}

async function* toolReadFile(
  filePath: string,
  preview: string,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  yield ev("tool_call", { name: "read_file", args: { file_path: filePath } });
  await sleep(380 + Math.random() * 250, signal);
  yield ev("tool_result", { name: "read_file", preview });
}

function debugEvent(tid: string, t0: number): SSEEvent {
  return ev("debug", {
    thread_id: tid,
    inference_id: "mock-inf-" + rid(),
    trace_id: "mock-tr-" + rid(),
    execution_time_ms: Date.now() - t0,
  });
}

function pendingApprovalEvent(
  tid: string,
  action: ActionRequest,
  allowed: ("approve" | "edit" | "reject")[] = ["approve", "edit", "reject"]
): SSEEvent {
  return ev("pending_approval", {
    thread_id: tid,
    interrupt_id: "mock-int-" + rid(),
    action_requests: [action],
    review_configs: [{ action_name: action.name, allowed_decisions: allowed }],
  });
}

function writeFileAction(
  task: TaskEditorState,
  description: string
): ActionRequest {
  return {
    name: "write_file",
    args: {
      file_path: "/task.json",
      content: JSON.stringify(task, null, 2),
    },
    description,
  };
}

// =========================
// Scenario: Add Quiz action to last block
// =========================

async function* addQuizScenario(
  tid: string,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const session = getSession(tid);
  const t0 = Date.now();

  yield* toolReadFile(
    "/guides/01-components.md",
    MOCK_GUIDES.components,
    signal
  );

  yield* streamReply(
    "마지막 블록에 button_quiz 액션을 추가할게요. " +
      "옵션 3개 중 1개를 정답으로 표시해두었습니다.",
    signal
  );

  const base = ensureCurrentTask(session);
  const lastBlock = base.blocks[base.blocks.length - 1];

  let nextTask: TaskEditorState;

  if (lastBlock && lastBlock.action === null) {
    // 마지막 블록의 action 슬롯에 button_quiz 부여
    const quizAction: TaskEditorActionComponent = {
      type: "button_quiz",
      incorrect_message: QUIZ_INCORRECT,
      options: [
        { text: "오답 1", is_correct: false },
        { text: "정답", is_correct: true },
        { text: "오답 2", is_correct: false },
      ],
    };
    const updatedBlock: TaskEditorBlock = {
      ...lastBlock,
      action: quizAction,
    };
    nextTask = {
      ...base,
      blocks: [...base.blocks.slice(0, base.blocks.length - 1), updatedBlock],
    };
  } else {
    // 마지막 블록에 이미 action 이 있으면 새 블록 append
    const newBlock: TaskEditorBlock = {
      type: "body",
      description: [
        {
          type: "text",
          content: "다음 중 올바른 것은 무엇일까요?",
          typography_style: "label",
        },
      ],
      action: {
        type: "button_quiz",
        incorrect_message: QUIZ_INCORRECT,
        options: [
          { text: "오답 1", is_correct: false },
          { text: "정답", is_correct: true },
          { text: "오답 2", is_correct: false },
        ],
      },
    };
    nextTask = {
      ...base,
      blocks: [...base.blocks, newBlock],
    };
  }

  session.currentTask = nextTask;

  const action = writeFileAction(
    nextTask,
    "button_quiz 액션 추가 (옵션 3개, 정답 index 1)"
  );
  session.pendingAction = action;
  yield pendingApprovalEvent(tid, action);
  yield debugEvent(tid, t0);
}

// =========================
// Scenario: Remove last block
// =========================

async function* removeLastBlockScenario(
  tid: string,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const session = getSession(tid);
  const t0 = Date.now();

  yield* toolReadFile("/task.json", "(현재 task 구조 확인)", signal);

  const base = ensureCurrentTask(session);

  if (base.blocks.length <= 1) {
    yield* streamReply(
      "삭제할 수 있는 블록이 없어요. 블록이 1개만 남아 있습니다.",
      signal
    );
    yield ev("final", {
      thread_id: tid,
      assistant_message: "삭제할 블록이 없습니다.",
      task: base,
      task_changed: false,
    });
    yield debugEvent(tid, t0);
    return;
  }

  const target = base.blocks[base.blocks.length - 1];
  // 블록 라벨: description 첫 text content (max 20자) 또는 action type
  const firstText = target.description.find((c) => c.type === "text");
  const contentSnippet =
    firstText && firstText.type === "text"
      ? firstText.content.slice(0, 20)
      : "";
  const actionLabel = target.action
    ? `action: ${target.action.type}`
    : "action: null";
  const blockLabel = contentSnippet
    ? `"${contentSnippet}" (${actionLabel})`
    : `블록 (description ${target.description.length}개, ${actionLabel})`;

  yield* streamReply(`마지막 블록 ${blockLabel} 을 삭제할게요.`, signal);

  const nextTask: TaskEditorState = {
    ...base,
    blocks: base.blocks.slice(0, base.blocks.length - 1),
  };
  session.currentTask = nextTask;

  const action = writeFileAction(nextTask, `마지막 블록 1개 삭제`);
  session.pendingAction = action;
  yield pendingApprovalEvent(tid, action);
  yield debugEvent(tid, t0);
}

// =========================
// Scenario: Raw text → "거리두기란 무엇인가" 활동지
// =========================

// Source: RAW_TEXT_DISTANCING (components.tsx).
// raw text 의 paragraph 단위 (소제목/기본/버튼) = block 1 개.
// RAIN step 들처럼 markup 이 없는 줄은 직전 `기본:` block 의 description 에 추가.
function buildDistancingTask(): TaskEditorState {
  return makeMockTask({
    task_id: "",
    title: "거리두기란 무엇인가",
    set_number: 2,
    is_key_activity: true,
    blocks: [
      // 마음의 선글라스
      {
        description: [
          {
            type: "text",
            content: "마음의 선글라스",
            typography_style: "header",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "마음이 건강하지 않을 때는 세상을 마치 새까만 선글라스를 끼고 보는 것과 같습니다. 아무리 기분 좋은 일이 생겨도 결국 불안이나 걱정, 깊은 무력감에 빠지고 맙니다. 뇌가 모든 상황을 부정적으로 해석하도록 습관이 들어버렸기 때문입니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "이런 상태에서는 머릿속에 떠오르는 부정적인 생각들이 모두 변함없는 진실처럼 느껴집니다. 그 생각들을 곧 '나 자신'이라고 굳게 믿으며 스스로를 옭아매고 자책하게 되죠. 혹시 지금 이렇게 어둡고 긴 터널을 홀로 걷고 계시나요?",
            typography_style: "body",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "그렇다면 정말 잘 찾아오셨습니다. 이제부터 그 까만 마음의 선글라스를 벗고 본래의 삶을 되찾는 연습을 시작해보려 합니다. 나를 괴롭히던 그 생각들이 사실 '나' 자신이 아니었다는 것을 자연스럽게 깨닫게 될 것입니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      // 신성한 멈춤
      {
        description: [
          { type: "text", content: "신성한 멈춤", typography_style: "header" },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "생각은 순식간에 지나갑니다. 그런 생각을 미처 알아차리기도 전에 우린 이미 그에 반응해서 감정과 감각을 느끼며 어떤 행동을 마친 상태이지요. 이렇게 무의식적으로 반응하는 악순환을 끊어내려면 가장 먼저 '잠시 멈추는 연습'이 필요합니다. 이를 일상 속의 '신성한 멈춤'이라고 합니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "신성한 멈춤을 갖게 되면, 생각과 자동 반응 사이의 연결고리가 끊어집니다. 비로소 내 진짜 감정이 두려움인지 슬픔인지 한 걸음 물러서서 관찰할 수 있는 틈이 생기는 것이죠. 다툼이나 갈등이 일어나는 순간에도, 이 멈춤은 현명한 선택을 내릴 수 있는 마음의 여유를 만들어줍니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "이러한 멈춤은 마음이 비교적 평온할 때 일상 속에서 미리 연습해 두는 것이 좋습니다. 평소에 충분히 훈련해 두면, 거대한 스트레스가 닥쳐와도 흔들림 없이 차분하게 대처할 수 있습니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      // pause_quiz (정답 D)
      {
        description: [
          {
            type: "text",
            content:
              "무의식적인 반응을 끊어내기 위해 가장 먼저 해야 할 일은 무엇일까요?",
            typography_style: "label",
          },
        ],
        action: {
          type: "button_quiz",
          incorrect_message: QUIZ_INCORRECT,
          options: [
            { text: "내 잘못이 무엇인지 자책한다.", is_correct: false },
            { text: "반박할 말을 머릿속으로 계산한다.", is_correct: false },
            { text: "긍정적인 상황이라고 세뇌한다.", is_correct: false },
            {
              text: "마음속에서 일어나는 일들을 관찰한다.",
              is_correct: true,
            },
          ],
        },
      },
      {
        description: [
          {
            type: "text",
            content:
              "정답은 D입니다. '신성한 멈춤'을 통해 습관적으로 반응하는 연결고리를 끊어낼 수 있습니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      // 마음의 RAIN
      {
        description: [
          { type: "text", content: "마음의 RAIN", typography_style: "header" },
        ],
        action: null,
      },
      // 기본 + (markup 없는 RAIN step 들은 같은 block 에 부속)
      {
        description: [
          {
            type: "text",
            content:
              "그렇다면 이 '신성한 멈춤'은 어떻게 실천할 수 있을까요? 신성한 멈춤은 RAIN이라는 방법을 통해 실천할 수 있습니다. RAIN의 과정은 다음과 같습니다.",
            typography_style: "body",
          },
          {
            type: "text",
            content: "인지하기 (R, Recognition)",
            typography_style: "label",
          },
          {
            type: "text",
            content: "내 마음에서 무슨 일이 일어나는지 알아차리기",
            typography_style: "body",
          },
          {
            type: "text",
            content: "수용하기 (A, Acceptance)",
            typography_style: "label",
          },
          {
            type: "text",
            content:
              "마음속에 떠오른 감정이나 생각을 거부하지 않고 온전히 받아들이기",
            typography_style: "body",
          },
          {
            type: "text",
            content: "살펴보기 (I, Investigation)",
            typography_style: "label",
          },
          {
            type: "text",
            content:
              "그 마음의 움직임을 어린아이와 같은 호기심으로 가만히 살펴보기",
            typography_style: "body",
          },
          {
            type: "text",
            content: "탈동일시 (N, Non-identification)",
            typography_style: "label",
          },
          {
            type: "text",
            content: "그 생각들을 나 자신과 동일시하지 않기",
            typography_style: "body",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "이 네 가지 단계를 거치다 보면, 거센 생각의 소용돌이 속에서도 내가 진정으로 원하는 삶의 방향을 선택할 힘을 얻게 됩니다. 마음에 비바람이 몰아칠 때마다 이 'RAIN'을 떠올려 보세요. 해묵은 마음의 습관과 문제 행동에서 벗어나 자유로워지는 열쇠가 되어줄 것입니다. 앞으로 이 네 가지 과정에 따라 거리두기 기술을 차근차근 연습해 보겠습니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      // 건강한 마음에 대한 오해
      {
        description: [
          {
            type: "text",
            content: "건강한 마음에 대한 오해",
            typography_style: "header",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "흔히 마음이 건강해진다는 것을 '늘 긍정적인 생각만 가득한 상태'로 오해하곤 합니다. 하지만 억지로 긍정적인 생각만 하려 애쓰는 것은 그리 좋은 방법이 아닙니다. 일시적인 눈속임에 불과할 뿐만 아니라, 오히려 문제를 악화시킬 수 있기 때문입니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "가끔 \"아무 생각도 하지 마. 그럼 편해져\"라고 말하는 사람들도 있습니다. 하지만 생각을 완전히 없애는 것은 애초에 불가능합니다. 사람의 뇌는 무언가를 '지우는' 방식으로는 작동하지 않으니까요. 따라서 이 역시 효과적인 해결책이 될 수 없습니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "이 부분은 나중에 더 자세히 다루겠습니다. 지금은 앞으로 연습할 내용이 '무조건 긍정적으로 생각하기'나 '아무 생각도 하지 않기'가 아니라는 점만 기억해 두시면 충분합니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      // 새로운 관계
      {
        description: [
          { type: "text", content: "새로운 관계", typography_style: "header" },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "우리를 견딜 수 없이 힘들게 만드는 진짜 원인은 슬픔이나 불안 같은 '감정' 그 자체가 아닙니다. 그 감정을 다루고 관계 맺는 '방식'에 있습니다. 부정적인 감정이나 생각을 억누르고 없애려고만 하면, 그것들은 더 강하게 마음을 옭아맵니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "이제부터는 억지로 생각을 지우려 애쓰는 대신, 마음속에 떠오르는 생각들과 '건강하게 관계 맺는 연습'을 해나갈 것입니다. 생각과 적당한 거리를 두고 유연하게 대처할 수 있게 된다면, 앞으로 어떤 시련이 닥쳐와도 내 삶을 지혜롭게 이끌어갈 수 있을 것입니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      // newrel_quiz (정답 C)
      {
        description: [
          {
            type: "text",
            content: "오늘부터 새롭게 연습해 나갈 내용은 무엇일까요?",
            typography_style: "label",
          },
        ],
        action: {
          type: "button_quiz",
          incorrect_message: QUIZ_INCORRECT,
          options: [
            {
              text: "매일 스스로에게 긍정적인 말 해주기",
              is_correct: false,
            },
            { text: "아무 생각 없이 머릿속을 비워내기", is_correct: false },
            {
              text: "마음속 생각 및 감정들과 건강한 관계 맺기",
              is_correct: true,
            },
          ],
        },
      },
      {
        description: [
          {
            type: "text",
            content:
              "정답은 C입니다. 억지로 긍정적인 생각을 하거나 생각을 지우는 것은 불가능하며 효과적이지도 않습니다. 유일한 해결책은 내 생각과 감정을 대하는 관점을 바꾸는 것입니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
    ],
  });
}

async function* rawToDistancingScenario(
  tid: string,
  rawText: string,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const session = getSession(tid);
  const t0 = Date.now();

  yield ev("tool_call", { name: "ls", args: { path: "/guides" } });
  await sleep(400, signal);
  yield ev("tool_result", {
    name: "ls",
    preview:
      "00-overview.md\n01-components.md\n02-structure.md\n03-tone.md\n04-styles.md\nexamples/\n  day1-start-of-change.json\n  day2-distancing.json\n  day3-experience-separation.json",
  });

  yield* toolReadFile("/guides/02-structure.md", MOCK_GUIDES.structure, signal);
  yield* toolReadFile(
    "/guides/01-components.md",
    MOCK_GUIDES.components,
    signal
  );
  yield* toolReadFile(
    "/guides/examples/day2-distancing.json",
    '{\n  "task_id": "day2-distancing",\n  "title": "거리두기란 무엇인가",\n  "blocks": [\n    section header 블록 × 5 (마음의 선글라스 / 신성한 멈춤 / 마음의 RAIN / 건강한 마음에 대한 오해 / 새로운 관계),\n    button_quiz × 2 (pause_quiz 정답D / newrel_quiz 정답C),\n    정답 해설 블록 × 2\n  ]\n}',
    signal
  );

  const charCount = rawText.length;

  yield* streamReply(
    `${charCount}자, "거리두기란 무엇인가" 활동지로 분석됩니다.\n\n` +
      `소스 마크업 매핑:\n` +
      `· "소제목" → section header 블록 (description[0] = text(header))\n` +
      `· "기본" → text(body) (같은 블록의 description 에 추가)\n` +
      `· "버튼" + 선택지 + "활동지를 천천히..." → button_quiz (incorrect_message 포함)\n` +
      `· RAIN 4단계 (R/A/I/N) → text(label) + text(body) 페어 × 4, 마음의 RAIN 블록 description 안에 배치\n\n` +
      `구조 설계 (v2, 9블록):\n` +
      `• section header 블록 × 5 — 마음의 선글라스 / 신성한 멈춤 / 마음의 RAIN / 건강한 마음에 대한 오해 / 새로운 관계\n` +
      `• button_quiz 블록 × 2 — pause_quiz (정답 D, 4 options), newrel_quiz (정답 C, 3 options)\n` +
      `• 정답 해설 블록 × 2 — text(body)\n\n` +
      `RAIN 정의는 label-body 페어로 4번 반복해 시각적 위계를 확보했습니다.`,
    signal
  );

  const draftTask = mergeSessionMetadata(
    buildDistancingTask(),
    session.currentTask
  );
  session.currentTask = draftTask;

  const action = writeFileAction(
    draftTask,
    `"거리두기란 무엇인가" 활동지 — 9개 블록. ` +
      `section header 블록 × 5, button_quiz × 2, 정답 해설 블록 × 2. ` +
      `RAIN 4단계 label/body 위계.`
  );
  session.pendingAction = action;
  yield pendingApprovalEvent(tid, action);
  yield debugEvent(tid, t0);
}

// =========================
// Scenario: Raw text → "기술 1: 경험 분리" 활동지
// =========================

// Source: RAW_TEXT_EXPERIENCE_SEPARATION (components.tsx).
// raw text 의 paragraph 단위 (소제목/기본/버튼) = block 1 개.
// 이미지 (🖼️) 와 markup 없는 줄들 (생각/감정/신체 반응/행동 충동) 은 직전 `기본:` block 의 description 에 추가.
function buildExperienceSeparationTask(): TaskEditorState {
  return makeMockTask({
    task_id: "",
    title: "기술 1: 경험 분리",
    set_number: 3,
    is_key_activity: true,
    blocks: [
      // 첫 번째 훈련법
      {
        description: [
          {
            type: "text",
            content: "첫 번째 훈련법",
            typography_style: "header",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "지난 시간에 마음을 인지하는 두 가지 훈련 방법이 있다고 배웠습니다. 기록을 통해 경험을 분리하는 연습과, 명상으로 주의력을 통제하는 훈련이었죠. 오늘은 그중 첫 번째 연습을 본격적으로 시작해 보겠습니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "이번 시간에 가장 먼저 배울 핵심 개념은 심리학에서 매우 중요하게 다루는 '경험 분리'입니다. 마음의 작용을 이해하기 위한 아주 기초적인 작업입니다. 스포츠나 악기를 처음 배울 때 기본기가 중요한 것처럼, 이 기본기만 확실히 익혀두어도 마음과 거리 두는 능력이 몰라보게 탄탄해질 것입니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      // 복잡한 경험 꾸러미
      {
        description: [
          {
            type: "text",
            content: "복잡한 경험 꾸러미",
            typography_style: "header",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "마음속에 떠오르는 부정적인 생각과 거리를 둔 채 가만히 바라보기란 말처럼 쉽지 않습니다. 현실에서 마주하는 내면의 경험이 단순히 '생각'이라는 단일한 덩어리로만 이루어져 있지 않기 때문입니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      // 기본 + (markup 없는 🖼️ image 는 같은 block 에 부속)
      {
        description: [
          {
            type: "text",
            content:
              "사실 마음속에서는 생각과 감정, 신체 반응, 그리고 행동과 충동이 복잡하게 뒤섞여 하나의 거대한 '꾸러미'처럼 한꺼번에 쏟아집니다.",
            typography_style: "body",
          },
          {
            type: "image",
            content:
              "https://placehold.co/600x300?text=상황+%E2%86%92+경험+꾸러미",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              '예를 들어 직장에서 무언가 실수를 한 상황을 떠올려 볼까요? "이번 프로젝트는 나 때문에 다 망했어"라는 생각과 동시에 죄책감이나 불안 같은 강렬한 감정이 파도처럼 밀려옵니다. 가슴이 답답해지거나 머리가 아픈 증상이 나타나기도 하고, 당장이라도 다 포기하고 도망치고 싶다는 충동까지 일어납니다. 이 모든 것이 하나의 꾸러미로 묶여서 다가오는 것이죠.',
            typography_style: "body",
          },
        ],
        action: null,
      },
      // bundle_quiz (정답 D)
      {
        description: [
          {
            type: "text",
            content:
              "다음 중 내면에서 뭉쳐지는 '경험 꾸러미'의 구성 요소가 아닌 것은 무엇일까요?",
            typography_style: "label",
          },
        ],
        action: {
          type: "button_quiz",
          incorrect_message: QUIZ_INCORRECT,
          options: [
            {
              text: "머릿속을 스쳐가는 부정적인 '생각'",
              is_correct: false,
            },
            { text: "불안하고 초조한 '감정'", is_correct: false },
            { text: "도망치고 싶어지는 '충동'", is_correct: false },
            { text: "실수를 저지른 객관적인 '상황'", is_correct: true },
          ],
        },
      },
      {
        description: [
          {
            type: "text",
            content:
              "정답은 D입니다. 실수를 저지른 '상황'은 내면의 반응을 촉발하는 외부 사건일 뿐, 경험 꾸러미에 속하지 않습니다. 경험 꾸러미는 특정 상황을 마주했을 때 마음속에서 덩어리져 일어나는 생각, 감정/신체 반응, 행동/충동으로 구성됩니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      // 끝없는 악순환
      {
        description: [
          {
            type: "text",
            content: "끝없는 악순환",
            typography_style: "header",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "경험 꾸러미 속의 생각, 감정과 신체 반응, 행동과 충동은 멈춰 있지 않고 서로 끊임없이 영향을 주고받습니다. 부정적인 생각이 불안을 증폭시키고, 그 불안은 호흡을 가쁘게 만들어 더 극단적인 생각을 불러오기도 합니다. 마치 엉켜버린 털실 타래처럼 단단하게 뭉쳐서 끝없는 악순환을 만들어냅니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "이렇게 얽힌 꾸러미를 그대로 둔 채 생각과 거리를 두기란 거의 불가능합니다. 마음속에 정확히 어떤 생각이 스쳐 지나갔는지 파악하기 어려울 뿐만 아니라, 막연한 감정의 크기가 너무 압도적으로 느껴지기 때문입니다. 따라서 이 거대한 덩어리를 각각의 개별 요소로 가닥가닥 쪼개어 바라보는 연습이 필요합니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      // 기본 + (markup 없는 생각/감정/신체 반응/행동 충동 label-body pair 는 같은 block 에 부속)
      {
        description: [
          {
            type: "text",
            content:
              "위의 직장에서 무언가 실수를 한 상황 예시에서 '경험 꾸러미'를 분리한 모습은 다음과 같습니다.",
            typography_style: "body",
          },
          { type: "text", content: "생각", typography_style: "label" },
          {
            type: "text",
            content: "이번 프로젝트는 나 때문에 다 망했어",
            typography_style: "body",
          },
          { type: "text", content: "감정", typography_style: "label" },
          {
            type: "text",
            content: "죄책감, 불안",
            typography_style: "body",
          },
          { type: "text", content: "신체 반응", typography_style: "label" },
          {
            type: "text",
            content: "가슴이 답답함, 머리가 아픔",
            typography_style: "body",
          },
          { type: "text", content: "행동/충동", typography_style: "label" },
          {
            type: "text",
            content: "포기하고 도망치고 싶었음",
            typography_style: "body",
          },
        ],
        action: null,
      },
      // 실전 연습
      {
        description: [
          { type: "text", content: "실전 연습", typography_style: "header" },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "이처럼 얽혀 있는 마음의 덩어리를 개별 요소로 해체하는 과정을 '경험 분리'라고 부릅니다. 방법은 간단하지만 효과는 매우 강력합니다. 압도적인 감정에 휩쓸리지 않고 마음을 객관적으로 바라볼 수 있게 돕기 때문입니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "앞으로 벅차고 강렬한 감정을 마주하더라도, 일기장에 감정을 쏟아내듯 무작정 적어 내려가는 것은 피하는 게 좋습니다. 그 대신, 객관적인 '상황', 머릿속을 스쳐 간 '생각', 느껴진 '감정과 신체 반응', 그리고 '충동이나 실제 행동'을 명확히 나누어 기록해 보세요.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "이렇게 항목을 구분해 적는 것만으로도 혼란스러웠던 경험 꾸러미의 힘이 약해지고 마음에 여유가 생기는 것을 느낄 수 있습니다. 자, 그럼 다음 활동에서 이 '경험 분리'를 구체적으로 어떻게 실천할 수 있는지 함께 알아보겠습니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
    ],
  });
}

async function* rawToExperienceSeparationScenario(
  tid: string,
  rawText: string,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const session = getSession(tid);
  const t0 = Date.now();

  yield ev("tool_call", { name: "ls", args: { path: "/guides" } });
  await sleep(400, signal);
  yield ev("tool_result", {
    name: "ls",
    preview:
      "00-overview.md\n01-components.md\n02-structure.md\n03-tone.md\n04-styles.md\nexamples/\n  day1-start-of-change.json\n  day2-distancing.json\n  day3-experience-separation.json",
  });

  yield* toolReadFile("/guides/02-structure.md", MOCK_GUIDES.structure, signal);
  yield* toolReadFile(
    "/guides/01-components.md",
    MOCK_GUIDES.components,
    signal
  );
  yield* toolReadFile(
    "/guides/examples/day3-experience-separation.json",
    '{\n  "task_id": "day3-experience-separation",\n  "title": "기술 1: 경험 분리",\n  "blocks": [\n    section header 블록 × 4,\n    image 컴포넌트 (꾸러미 도식) 포함 블록 × 1,\n    button_quiz × 1 (bundle_quiz 정답D),\n    정답 해설 블록 × 1,\n    경험 분리 예시 (label+body × 4 페어) 블록\n  ]\n}',
    signal
  );

  const charCount = rawText.length;

  yield* streamReply(
    `${charCount}자, "기술 1: 경험 분리" 활동지로 분석됩니다.\n\n` +
      `소스 마크업의 특이점:\n` +
      `· 이미지 placeholder → image 컴포넌트 (https://placehold.co URL), 복잡한 경험 꾸러미 블록의 description 안에 배치\n` +
      `· "생각/감정/신체 반응/행동·충동" 4-항목 분리 예시 → text(label) + text(body) 페어 × 4, 끝없는 악순환 블록의 description 안에 배치\n` +
      `· "버튼" + 4 선택지 → button_quiz (정답 D, 4 options, incorrect_message 포함)\n\n` +
      `구조 설계 (v2, 6블록):\n` +
      `• section header 블록 × 4 — 첫 번째 훈련법 / 복잡한 경험 꾸러미 / 끝없는 악순환 / 실전 연습\n` +
      `• button_quiz 블록 × 1 (bundle_quiz, 정답 D)\n` +
      `• 정답 해설 블록 × 1`,
    signal
  );

  const draftTask = mergeSessionMetadata(
    buildExperienceSeparationTask(),
    session.currentTask
  );
  session.currentTask = draftTask;

  const action = writeFileAction(
    draftTask,
    `"기술 1: 경험 분리" 활동지 — 6개 블록. ` +
      `section header 블록 × 4, button_quiz × 1 (bundle_quiz), 정답 해설 블록 × 1. ` +
      `image 컴포넌트 × 1 (꾸러미 도식 placeholder).`
  );
  session.pendingAction = action;
  yield pendingApprovalEvent(tid, action);
  yield debugEvent(tid, t0);
}

// =========================
// Scenario: Raw text → "변화의 시작 (Day 1)" 활동지
// =========================

// Source: RAW_TEXT_BEGINNING (components.tsx).
// raw text 의 paragraph 단위 (소제목/기본/버튼) = block 1 개.
function buildBeginningOfChangeTask(): TaskEditorState {
  return makeMockTask({
    task_id: "",
    title: "변화의 시작",
    set_number: 1,
    is_key_activity: true,
    blocks: [
      // 위대한 선택
      {
        description: [
          { type: "text", content: "위대한 선택", typography_style: "header" },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "반갑습니다. 아마 마음이 심하게 흔들리고 힘든 시기를 겪다가 이곳까지 찾아오셨을 겁니다. 그리고 이제는 정말 스스로 변해야겠다고 굳게 마음먹으셨겠지요. 그렇지 않았다면 디스턴싱이 아니라 당장 눈앞의 고통만 가려줄 임시방편을 택하셨을 테니까요.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "진정한 변화를 일궈내겠다고 결심한 그 용기에 진심으로 박수를 보냅니다. 앞으로 100일 동안 이곳에서 내면의 힘을 기르며, 인생의 전환점을 맞이하시게 될 겁니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      // 마법의 알약
      {
        description: [
          { type: "text", content: "마법의 알약", typography_style: "header" },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "이곳에는 복잡한 감정을 단숨에 잠재워 줄 마법의 알약은 없습니다. 그저 글 몇 편 읽는 것만으로 엄청난 변화가 일어난다는 헛된 약속도 하지 않겠습니다. 마음을 치유하는 일은 하루아침에 뚝딱 이루어지는 가벼운 과정이 아닙니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "하지만 이 프로그램은 당신이 스스로의 치료자가 되어 진정한 변화를 이끌어내도록 도울 수는 있습니다. 이 과정은 상담사나 약물에 기대는 것이 아니라, 내 삶을 이끄는 방법을 배우는 주도적인 훈련이 될 것입니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "때로는 이 훈련의 과정 속에서 답답하고 좌절감을 느낄지도 모릅니다. 하지만 인내심을 가지고 끝까지 해낸다면, 그 무엇과도 바꿀 수 없는 단단한 내면의 힘을 얻게 될 것입니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "여지껏 삶의 전환점을 맞이한 수많은 사람들도 처음에는 과연 자신이 변할 수 있을지 의심했습니다. 지금 이 단계에서 그런 의문이 드는 것은 매우 당연한 일입니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              '"나도 변화할 수 있을까?" 해보지 않으면 모릅니다. 제대로 해보지 않고 평생 "나도 변화할 수 있을까?"라는 의문을 품고 사시겠습니까, 아니면 이번 기회에 나도 변화할 수 있는지 확실히 알아보시겠습니까?',
            typography_style: "body",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "변화하기로 마음 먹고 여기까지 온 것이라면, 지금부터는 스스로를 믿고 프로그램을 밟아 나아가보시길 바랍니다. 포기하고 싶은 순간이 찾아오면 언제든 코치 선생님에게 알려주세요. 코치 선생님은 당신이 이 여정을 계속해 나갈 수 있도록 최선을 다해 도울 것입니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      // 마음속 타인
      {
        description: [
          { type: "text", content: "마음속 타인", typography_style: "header" },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "누군가 내 옆을 졸졸 따라다니며 부정적이고 비판적인 말을 귓가에 끊임없이 속삭인다고 상상해 보세요. 처음엔 당황해서 꾹 참아보기도 하고, 그래도 멈추지 않으면 조용히 하라고 화도 내보겠지요. 하지만 그는 아랑곳하지 않습니다. 밀어내려 싸우고 애원해 보지만, 결국 괴로워지는 건 나 자신뿐입니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "그 목소리는 내가 어찌할 수 없는 것입니다. 하지만 우리는 이러한 사실을 모른채 스스로에게 끔찍한 일을 저지르고 있습니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "우린 마음속에 떠오르는 모든 생각과 감정을 밀어내려고 온갖 수를 쓰며 애를 씁니다. 그러다 결국엔 그런 부정적인 목소리를 내는 자신에게 화를 내고, 그것을 막지 못하는 스스로를 자책하며 갉아먹고 있는 것이죠.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "앞으로 100일 동안 다룰 핵심 기술은 바로 '거리두기'입니다. 쉽게 말해, 머릿속에 떠오르는 생각들을 내 옆에 앉은 낯선 사람이 던지는 말이라고 여기는 연습입니다. 이 기술에 익숙해지면 생각을 관찰할 수 있게 되고, 귓가에 어떤 소리가 맴돌든 그로부터 온전히 자유로워질 수 있습니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "단, 이것이 단순히 '주변의 잡음처럼 하찮게 여기며 무시하기'를 뜻하진 않는다는 것을 꼭 기억해주세요. 디스턴싱은 그 반대입니다. 하찮게 여기지 않을 것입니다. 그렇다고 소중히 여기지도 않을 것입니다. 판단과 평가 없이 듣고, 있는 그대로를 기꺼이 품을 것입니다. 우리는 앞으로 이 태도를 연습해 나갈 것입니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      // stranger_quiz (정답 B)
      {
        description: [
          {
            type: "text",
            content:
              "디스턴싱을 배우고 나면, 머릿속에 스스로를 깎아내리는 비관적인 생각이 떠올랐을 때 어떻게 대처하게 될까요?",
            typography_style: "label",
          },
        ],
        action: {
          type: "button_quiz",
          incorrect_message: QUIZ_INCORRECT,
          options: [
            {
              text: "그 생각이 나의 진짜 모습이자 진실이라고 굳게 믿는다.",
              is_correct: false,
            },
            {
              text: "나와 무관한 주변의 소리로 여기며 판단 없이 지그시 관찰한다.",
              is_correct: true,
            },
            {
              text: "왜 그런 생각을 했는지 스스로를 탓하며 분석한다.",
              is_correct: false,
            },
          ],
        },
      },
      {
        description: [
          {
            type: "text",
            content:
              "정답은 B입니다. 생각은 나의 본질을 의미하지 않습니다. 그저 지금 창 밖에서 들려오는 바람 소리처럼 우연히 스쳐 지나가는 것일 뿐입니다. 이 사실을 꼭 기억해주시길 바랍니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      // 오래된 펌프
      {
        description: [
          { type: "text", content: "오래된 펌프", typography_style: "header" },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "낯선 개념을 받아들이며 습관을 바로잡는 과정은 오랫동안 쓰지 않은 펌프에서 물을 끌어올리는 일과 비슷합니다. 펌프를 3년이나 방치했다면 그 안에는 엄청난 녹과 먼지가 쌓여 있겠지요. 즉, 누구나 거리두기를 연습하며 내면을 들여다보기 시작하면, 처음에는 흙먼지가 섞인 탁한 물이 쏟아져 나옵니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "많은 분들이 이 흙탕물을 보고는 상태가 더 나빠졌다고 지레짐작하며 서둘러 펌프질을 멈춰버립니다. 하지만 이는 고여 있던 찌꺼기가 빠져나오는, 아주 자연스럽고도 꼭 필요한 중간 과정일 뿐입니다. 지금 해야 할 일은 맑은 물이 나올 때까지 멈추지 않고 계속 펌프질을 하는 것입니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "당장 거리두기 연습이 고통스럽고 혼란스럽더라도 두려워하거나 멈추지 않아야 합니다. 내면의 탁한 습관들이 모두 흘러가도록 묵묵히 내버려 두면, 마침내 맑고 건강한 습관이 그 자리를 채울 것입니다. 맑은 물을 마시게 되는 그 감격스러운 순간까지 프로그램이 곁에서 돕겠습니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      // pump_quiz (정답 C)
      {
        description: [
          {
            type: "text",
            content:
              "훈련을 시작한 후, 갑자기 혼란스럽고 부정적인 감정이 쏟아져 나온다면 어떻게 해야 할까요?",
            typography_style: "label",
          },
        ],
        action: {
          type: "button_quiz",
          incorrect_message: QUIZ_INCORRECT,
          options: [
            {
              text: "상태가 악화되었다고 판단하고 즉시 훈련을 그만둔다.",
              is_correct: false,
            },
            {
              text: "더 쉽고 빠른 다른 치료법이 없는지 찾아 나선다.",
              is_correct: false,
            },
            {
              text: "자연스러운 과정이라 여기고 훈련을 계속한다.",
              is_correct: true,
            },
          ],
        },
      },
      {
        description: [
          {
            type: "text",
            content:
              "정답은 C입니다. 훈련 과정에서 내면의 찌꺼기가 빠져나오는 것은 자연스럽고 필연적인 현상입니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      // 100일간의 여정
      {
        description: [
          {
            type: "text",
            content: "100일간의 여정",
            typography_style: "header",
          },
        ],
        action: null,
      },
      {
        description: [
          {
            type: "text",
            content:
              "이 힘든 시기가 오히려 단단한 성장의 발판이 될 수 있도록 끝까지 함께하겠습니다. 프로그램을 성공적으로 마치게 된다면, 훗날 비슷한 시련이 다시 찾아와도 예전처럼 무너지지 않고 의연하게 이겨낼 수 있을 것입니다. 이 100일간의 뜻깊은 여정 끝에서, 한층 성숙하고 달라진 자신과 마주하시게 될 겁니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
      // journey_quiz (정답 B)
      {
        description: [
          {
            type: "text",
            content:
              "100일간의 디스턴싱 훈련 과정을 통해 얻을 수 있는 것은 무엇일까요?",
            typography_style: "label",
          },
        ],
        action: {
          type: "button_quiz",
          incorrect_message: QUIZ_INCORRECT,
          options: [
            {
              text: "당장의 불안과 스트레스를 잊게 해주는 도피처",
              is_correct: false,
            },
            {
              text: "근본적으로 나 자신을 변화시키는 내면의 힘",
              is_correct: true,
            },
            {
              text: "누군가의 조언으로 내 문제를 해결하는 것",
              is_correct: false,
            },
          ],
        },
      },
      {
        description: [
          {
            type: "text",
            content:
              "정답은 B입니다. 디스턴싱은 스스로 만들어내는 가장 확실하고 굳건한 변화를 추구합니다.",
            typography_style: "body",
          },
        ],
        action: null,
      },
    ],
  });
}

async function* rawToBeginningScenario(
  tid: string,
  rawText: string,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const session = getSession(tid);
  const t0 = Date.now();

  yield ev("tool_call", { name: "ls", args: { path: "/guides" } });
  await sleep(400, signal);
  yield ev("tool_result", {
    name: "ls",
    preview:
      "00-overview.md\n01-components.md\n02-structure.md\n03-tone.md\n04-styles.md\nexamples/\n  day1-start-of-change.json\n  perception-rain-r.json\n  allow-rain-a.json",
  });

  yield* toolReadFile("/guides/02-structure.md", MOCK_GUIDES.structure, signal);
  yield* toolReadFile(
    "/guides/01-components.md",
    MOCK_GUIDES.components,
    signal
  );
  yield* toolReadFile(
    "/guides/03-tone.md",
    `# Tone Guide

- 반말 X. 존댓말 사용
- "~합니다" 보다 "~해요" 가 자연스러움
- 1~2문장씩 짧게 끊어쓰기
- 사용자를 다그치지 않음 — 권유형 어미 ("~해볼까요")
- label 은 질문/지시, body 는 설명, header 는 섹션 제목`,
    signal
  );
  yield* toolReadFile(
    "/guides/examples/day1-start-of-change.json",
    '{\n  "task_id": "day1-start-of-change",\n  "title": "변화의 시작",\n  "blocks": [\n    section header 블록 × 5 (위대한 선택 / 마법의 알약 / 마음속 타인 / 오래된 펌프 / 100일간의 여정),\n    button_quiz 블록 × 3 (stranger_quiz 정답B / pump_quiz 정답C / journey_quiz 정답B),\n    정답 해설 블록 × 3\n  ]\n}',
    signal
  );

  const charCount = rawText.length;

  yield* streamReply(
    `${charCount}자, "변화의 시작" 활동지 (디스턴싱 100일 프로그램 Day 1) 로 분석됩니다.\n\n` +
      `소스 마크업을 v2 블록 위계에 매핑했어요:\n` +
      `· "제목" → title 필드 (블록으로 별도 생성 안 함)\n` +
      `· "소제목" → section header 블록 (description[0] = text(header))\n` +
      `· "기본" → text(body), 같은 블록의 description 에 추가\n` +
      `· "버튼" + 옵션 + "활동지를 천천히 살펴본 뒤 다시 답해보세요." → button_quiz (incorrect_message 포함)\n\n` +
      `구조 설계 (v2, 11블록):\n` +
      `• section header 블록 × 5 — 위대한 선택 / 마법의 알약 / 마음속 타인 / 오래된 펌프 / 100일간의 여정\n` +
      `• button_quiz 블록 × 3 — stranger_quiz (정답 B), pump_quiz (정답 C), journey_quiz (정답 B)\n` +
      `• 정답 해설 블록 × 3 — text(body)\n\n` +
      `Quiz 직후에는 가이드 패턴대로 정답 해설 블록을 배치했습니다.`,
    signal
  );

  const draftTask = mergeSessionMetadata(
    buildBeginningOfChangeTask(),
    session.currentTask
  );
  session.currentTask = draftTask;

  const action = writeFileAction(
    draftTask,
    `"변화의 시작" 활동지 (Day 1) — 11개 블록. ` +
      `section header 블록 × 5, button_quiz 블록 × 3, 정답 해설 블록 × 3. ` +
      `stranger_quiz / pump_quiz / journey_quiz, 모두 정답 해설 블록 동반.`
  );
  session.pendingAction = action;
  yield pendingApprovalEvent(tid, action);
  yield debugEvent(tid, t0);
}

// =========================
// Scenario: Branching flow with button_branch (sequence-based)
// =========================

async function* branchingFlowScenario(
  tid: string,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const session = getSession(tid);
  const t0 = Date.now();

  yield* toolReadFile("/guides/02-structure.md", MOCK_GUIDES.structure, signal);
  yield* toolReadFile(
    "/guides/01-components.md",
    MOCK_GUIDES.components,
    signal
  );
  yield* toolReadFile(
    "/guides/02-structure.md#branching",
    `## 분기 (button_branch) 규칙\n\n- show_from/show_to/restart_from/hide_from 은 block sequence (1-based).\n- 분기 대상 블록은 질문 블록 바로 뒤에 배치.\n- 질문 블록 sequence = N+1, yes 경로 = N+2, no 경로 = N+3.`,
    signal
  );

  const base = ensureCurrentTask(session);

  // base.blocks 끝에 3블록 append (sequence 계산)
  const N = base.blocks.length; // 현재 블록 수
  const seqQ = N + 1; // 질문 블록 sequence
  const seqYes = N + 2; // yes 경로 sequence
  const seqNo = N + 3; // no 경로 sequence

  yield* streamReply(
    `button_branch 분기를 추가할게요. v2 에서는 block sequence (1-based) 로 분기 대상을 참조합니다.\n` +
      `질문 블록 = sequence ${seqQ}, yes 경로 = ${seqYes}, no 경로 = ${seqNo}.\n` +
      `options[0].show_from = ${seqYes}, options[1].show_from = ${seqNo}.`,
    signal
  );

  const branchQuestionBlock: TaskEditorBlock = {
    type: "body",
    description: [
      {
        type: "text",
        content: "지금 그 감정을 잠시 머물게 둘 준비가 되셨나요?",
        typography_style: "label",
      },
    ],
    action: {
      type: "button_branch",
      options: [
        { text: "네, 시도해볼게요", show_from: seqYes },
        { text: "아직 어려워요", show_from: seqNo },
      ],
    },
  };

  const yesPathBlock: TaskEditorBlock = {
    type: "body",
    description: [
      {
        type: "text",
        content: "좋습니다. 1분간 그 감정에 이름표를 붙이고 지켜봐주세요.",
        typography_style: "body",
      },
    ],
    action: {
      type: "text_input",
      fields: [{ placeholder: "1분 후, 감정이 어떻게 변했나요?" }],
    },
  };

  const noPathBlock: TaskEditorBlock = {
    type: "body",
    description: [
      {
        type: "text",
        content:
          "괜찮아요. 어려운 게 자연스럽습니다. 다음에 다시 시도해도 좋아요.",
        typography_style: "body",
      },
      {
        type: "text",
        content: "지금 가장 부담스럽게 느껴지는 부분은 무엇인가요?",
        typography_style: "label",
      },
    ],
    action: {
      type: "text_input",
      fields: [{ placeholder: "솔직하게 적어주세요" }],
    },
  };

  const nextTask: TaskEditorState = {
    ...base,
    blocks: [...base.blocks, branchQuestionBlock, yesPathBlock, noPathBlock],
  };
  session.currentTask = nextTask;

  const action = writeFileAction(
    nextTask,
    `button_branch 분기 추가 — 질문 블록 (sequence ${seqQ}) + yes 경로 (${seqYes}) + no 경로 (${seqNo}). ` +
      `options[0].show_from = ${seqYes}, options[1].show_from = ${seqNo}.`
  );
  session.pendingAction = action;
  yield pendingApprovalEvent(tid, action);
  yield debugEvent(tid, t0);
}

// =========================
// Scenario: Validate task against v2 rules (read-only)
// =========================

async function* validateTaskScenario(
  tid: string,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const session = getSession(tid);
  const t0 = Date.now();

  yield* toolReadFile("/guides/02-structure.md", MOCK_GUIDES.structure, signal);
  yield ev("tool_call", {
    name: "read_file",
    args: { file_path: "/task.json" },
  });
  await sleep(400, signal);

  const base = ensureCurrentTask(session);
  yield ev("tool_result", {
    name: "read_file",
    preview: `(task with ${base.blocks.length} blocks)`,
  });

  // v2 규칙 lint
  type Issue = { level: "error" | "warn" | "ok"; msg: string };
  const issues: Issue[] = [];

  // 규칙 1: task_id non-empty + kebab-case (warn)
  if (!base.task_id || !base.task_id.trim()) {
    issues.push({ level: "warn", msg: "task_id 가 비어 있음" });
  } else if (!/^[a-z0-9][a-z0-9-]*$/.test(base.task_id)) {
    issues.push({
      level: "warn",
      msg: `task_id "${base.task_id}" 가 kebab-case 패턴이 아님`,
    });
  }

  // 규칙 2: title non-empty (error)
  if (!base.title || !base.title.trim()) {
    issues.push({ level: "error", msg: "title 이 비어 있음" });
  }

  // 규칙 3: blocks.length > 0 (error)
  if (base.blocks.length === 0) {
    issues.push({ level: "error", msg: "blocks 가 비어 있음" });
  }

  base.blocks.forEach((b, bi) => {
    const seq = bi + 1;

    // 규칙 4: 각 블록 description.length >= 1 (error)
    if (!b.description || b.description.length === 0) {
      issues.push({
        level: "error",
        msg: `블록 ${seq}: description 이 비어 있음`,
      });
      return;
    }

    // 규칙 4: description[0].type === "text" (error)
    if (b.description[0].type !== "text") {
      issues.push({
        level: "error",
        msg: `블록 ${seq}: description[0] 이 text 가 아님 (현재: ${b.description[0].type})`,
      });
    }

    // 규칙 5: description 중 text >= 1 (error)
    const textCount = b.description.filter((c) => c.type === "text").length;
    if (textCount === 0) {
      issues.push({
        level: "error",
        msg: `블록 ${seq}: description 에 text 컴포넌트가 없음`,
      });
    }

    // 규칙 6: action type 검증 (error)
    if (b.action !== null && b.action !== undefined) {
      const validTypes = [
        "text_input",
        "button_default",
        "button_quiz",
        "button_branch",
      ];
      if (!validTypes.includes(b.action.type)) {
        issues.push({
          level: "error",
          msg: `블록 ${seq}: action.type "${b.action.type}" 이 유효하지 않음`,
        });
      }

      // 규칙 7: text_input.fields.length >= 1 (error)
      if (b.action.type === "text_input") {
        const tiAction = b.action as Extract<
          TaskEditorActionComponent,
          { type: "text_input" }
        >;
        if (!tiAction.fields || tiAction.fields.length === 0) {
          issues.push({
            level: "error",
            msg: `블록 ${seq}: text_input.fields 가 비어 있음`,
          });
        }
      }

      // 규칙 8: button_quiz is_correct >= 1 (warn), incorrect_message non-empty (warn)
      if (b.action.type === "button_quiz") {
        const qAction = b.action as Extract<
          TaskEditorActionComponent,
          { type: "button_quiz" }
        >;
        const correctCount = qAction.options.filter(
          (o) => o.is_correct === true
        ).length;
        if (correctCount === 0) {
          issues.push({
            level: "warn",
            msg: `블록 ${seq}: button_quiz 에 is_correct: true 옵션이 없음`,
          });
        }
        if (!qAction.incorrect_message || !qAction.incorrect_message.trim()) {
          issues.push({
            level: "warn",
            msg: `블록 ${seq}: button_quiz.incorrect_message 가 비어 있음`,
          });
        }
      }

      // 규칙 9: button_branch show_from/show_to/restart_from/hide_from 범위 검증 (error)
      if (b.action.type === "button_branch") {
        const brAction = b.action as Extract<
          TaskEditorActionComponent,
          { type: "button_branch" }
        >;
        const total = base.blocks.length;
        brAction.options.forEach((opt, oi) => {
          const refs: [string, number | undefined][] = [
            ["show_from", opt.show_from],
            ["show_to", opt.show_to],
            ["restart_from", opt.restart_from],
            ["hide_from", opt.hide_from],
          ];
          refs.forEach(([key, val]) => {
            if (val !== undefined) {
              if (!Number.isInteger(val) || val < 1 || val > total) {
                issues.push({
                  level: "error",
                  msg: `블록 ${seq}: button_branch options[${oi}].${key} = ${val} 이 유효 범위 (1..${total}) 밖`,
                });
              }
            }
          });
        });
      }
    }

    // 규칙 10: text component content non-empty (warn), typography_style 유효 (error)
    b.description.forEach((c, ci) => {
      if (c.type === "text") {
        if (!c.content || !c.content.trim()) {
          issues.push({
            level: "warn",
            msg: `블록 ${seq} description[${ci}]: text content 가 비어 있음`,
          });
        }
        const validStyles = ["header", "body", "label"];
        if (!validStyles.includes(c.typography_style)) {
          issues.push({
            level: "error",
            msg: `블록 ${seq} description[${ci}]: typography_style "${c.typography_style}" 이 유효하지 않음`,
          });
        }
      }
    });
  });

  const errorCount = issues.filter((i) => i.level === "error").length;
  const warnCount = issues.filter((i) => i.level === "warn").length;

  const summaryLine =
    errorCount === 0 && warnCount === 0
      ? "✓ 모든 v2 규칙 통과"
      : `${errorCount === 0 ? "✓" : "✕"} error ${errorCount}, warn ${warnCount}`;

  const detail = issues.length
    ? "\n\n" +
      issues
        .map(
          (i) =>
            `${i.level === "error" ? "✕" : i.level === "warn" ? "⚠" : "✓"} ${i.msg}`
        )
        .join("\n")
    : "";

  yield* streamReply(
    `현재 활동지를 v2 규칙으로 검증했어요.\n\n` +
      `검사 항목 (10개): task_id kebab-case / title 비어있음 / blocks 비어있음 / ` +
      `description[0]=text / description text>=1 / action type 유효 / ` +
      `text_input fields>=1 / button_quiz is_correct+incorrect_message / ` +
      `button_branch sequence 범위 / text content+typography_style.\n\n` +
      `결과: ${summaryLine}${detail}\n\n` +
      `이 시나리오는 읽기 전용입니다 — 자동 수정은 하지 않아요.`,
    signal
  );

  yield ev("final", {
    thread_id: tid,
    assistant_message: `검증 완료. ${summaryLine}`,
    task: base,
    task_changed: false,
  });
  yield debugEvent(tid, t0);
}

// =========================
// Generic — help (mock 안내 + v2 컴포넌트 카탈로그)
// =========================

async function* genericScenario(
  tid: string,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const t0 = Date.now();
  yield* toolReadFile("/guides/00-overview.md", MOCK_GUIDES.components, signal);

  const reply =
    "━━━ Mock 모드 안내 ━━━\n" +
    "지금 채팅은 실서버 연결 없이 동작하는 mock 응답입니다.\n" +
    "`REACT_APP_AGENT_API_HOST` 환경변수가 설정되면 실서버 SSE 로 자동 전환됩니다.\n\n" +
    "━━━ 운영 Agent 역할 ━━━\n" +
    "DeepAgents + LangGraph 기반의 활동지(TaskEditorState) 작성·편집 어시스턴트.\n" +
    "내부적으로는 다음 가이드 문서를 참조해 작업합니다:\n" +
    "  · 00-overview.md — 전체 개요\n" +
    "  · 01-components.md — 컴포넌트 카탈로그 (아래 [컴포넌트] 참고)\n" +
    "  · 02-structure.md — 블록 invariant, 분기 규칙\n" +
    "  · 03-tone.md — 어투 가이드\n" +
    "  · 04-styles.md — typography_style 가이드\n" +
    "  · examples/*.json — 참고 활동지 예시들\n\n" +
    "[블록 (Block) — v2 기본 단위]\n" +
    "  블록은 type/id 없음. description 배열 + action(객체 1개 또는 null) 로 구성.\n" +
    "  invariant 3개:\n" +
    "    1. description[0] 은 text 컴포넌트 고정 (삭제·재정렬·타입 변경 불가)\n" +
    "    2. description.length >= 1, 그 중 text >= 1\n" +
    "    3. action 은 객체 1개 또는 null\n\n" +
    "[컴포넌트 8종 / UI 카테고리 5종]\n" +
    "  설명 섹션 (description[]):\n" +
    "    · text (텍스트) — content, typography_style: header|body|label\n" +
    "    · image (이미지) — content: URL\n" +
    "    · reference_automatic_thought (불러오기) — 자체 속성 없음\n" +
    "    · reference_coach_preset (불러오기) — preset_text\n" +
    "  유저 액션 섹션 (action):\n" +
    "    · text_input (텍스트 입력) — fields[]: {label?, placeholder?, help_text?}\n" +
    "    · button_default (버튼) — options[], allow_multiple_selection\n" +
    "    · button_quiz (버튼) — options[]{text, is_correct?}, incorrect_message\n" +
    "    · button_branch (버튼) — options[]{text, show_from?, show_to?, restart_from?, hide_from?}\n\n" +
    "[typography_style 3종]\n" +
    "  · header — 섹션 제목 (블록 description[0] 에 주로 사용)\n" +
    "  · body — 일반 본문 (기본)\n" +
    "  · label — 입력 필드 바로 위 질문/라벨\n\n" +
    "[button_branch 분기 참조]\n" +
    "  show_from / show_to / restart_from / hide_from 은 block sequence (1-based 정수).\n" +
    "  예: blocks[3] 을 가리키려면 show_from: 4.\n\n" +
    "[HITL — Human-In-The-Loop]\n" +
    "  · write_file / edit_file 호출 직전 자동 인터럽트 → Approve / Edit / Reject 결정\n" +
    "  · Reject 시 reason 을 ToolMessage 로 agent 에 전달, 다른 접근 시도\n" +
    "  · read_file / ls / grep 같은 정보 수집은 자동 진행\n\n" +
    "━━━ 현재 mock 시나리오 칩 ━━━\n" +
    "• 긴 글 → 활동지 (세 종류: 변화의 시작 / 거리두기란 무엇인가 / 기술 1: 경험 분리)\n" +
    "• 분기 추가 (button_branch + show_from 두 경로)\n" +
    "• 검증 (현재 task 를 v2 규칙 10개로 lint, 읽기 전용)\n" +
    "• Quiz 추가 (button_quiz)\n" +
    "• 마지막 블록 삭제\n\n" +
    "원하시는 작업을 자유롭게 적어주시거나, 아래 칩을 눌러보세요.";

  yield* streamReply(reply, signal);

  yield ev("final", {
    thread_id: tid,
    assistant_message: reply,
    task: getSession(tid).currentTask,
    task_changed: false,
  });
  yield debugEvent(tid, t0);
}

// =========================
// Resume
// =========================

export async function* mockResumeTaskAuthoring(
  req: ResumeRequest,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const tid = req.thread_id;
  const session = getSession(tid);
  const action = session.pendingAction;
  const decision: Decision | undefined = req.decisions[0];
  const t0 = Date.now();

  if (!action || !decision) {
    yield ev("error", {
      thread_id: tid,
      type: "no_pending_action",
      message: "No pending action to resume.",
    });
    yield debugEvent(tid, t0);
    return;
  }

  if (decision.type === "approve") {
    yield ev("tool_result", { name: action.name, preview: "OK" });
    await sleep(180, signal);
    const reply = "적용했어요. 미리보기에서 확인해보세요.";
    yield* streamReply(reply, signal);
    if (session.currentTask) {
      yield ev("task_patch", {
        thread_id: tid,
        task: { blocks: session.currentTask.blocks },
      });
    }
    yield ev("final", {
      thread_id: tid,
      assistant_message: reply,
      task: session.currentTask,
      task_changed: !!session.currentTask,
    });
  } else if (decision.type === "edit") {
    // edited_action 으로 task 재계산
    const updated = applyMockEdit(session.currentTask, decision.edited_action);
    session.currentTask = updated;
    yield ev("tool_result", {
      name: decision.edited_action.name,
      preview: "OK (edited)",
    });
    await sleep(180, signal);
    const reply = "수정하신 내용으로 적용했어요.";
    yield* streamReply(reply, signal);
    if (updated) {
      yield ev("task_patch", {
        thread_id: tid,
        task: { blocks: updated.blocks },
      });
    }
    yield ev("final", {
      thread_id: tid,
      assistant_message: reply,
      task: updated,
      task_changed: !!updated,
    });
  } else {
    // reject
    const why = decision.message?.trim() || "다른 방식으로 해주세요";
    const reply = `알겠어요. "${why}" — 다시 시도해볼게요.`;
    yield* streamReply(reply, signal);
    yield ev("final", {
      thread_id: tid,
      assistant_message: reply,
      task: session.currentTask,
      task_changed: false,
    });
  }

  yield debugEvent(tid, t0);
  session.pendingAction = undefined;
}
